import React, { useState, useEffect, useRef, useMemo } from 'react';
import './DesktopSearchModal.css';

function DesktopSearchModal({ isOpen, onClose, position, donors = {}, groups = [], savedLists = [], savedTables = [], feedEntries = [], onNavigate }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Search results
  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();

    // Search people by name and narrative
    const people = Object.entries(donors)
      .filter(([name, data]) =>
        name.toLowerCase().includes(q) ||
        data.narrative?.toLowerCase().includes(q)
      )
      .slice(0, 8)
      .map(([name, data]) => ({ name, ...data }));

    // Search groups by name or context
    const groupResults = groups
      .filter(g =>
        g.name?.toLowerCase().includes(q) ||
        g.context?.toLowerCase().includes(q)
      )
      .slice(0, 5);

    // Search lists by name, organization prompt, or group names
    const listResults = savedLists
      .filter(list =>
        list.name?.toLowerCase().includes(q) ||
        list.organizationPrompt?.toLowerCase().includes(q) ||
        list.groups?.some(g => g.name?.toLowerCase().includes(q)) ||
        list.groups?.some(g => g.people?.some(p => p.name?.toLowerCase().includes(q)))
      )
      .slice(0, 5);

    // Search tables by name or column names
    const tableResults = savedTables
      .filter(t =>
        t.name?.toLowerCase().includes(q) ||
        t.columns?.some(c => c.name?.toLowerCase().includes(q))
      )
      .slice(0, 5);

    // Search feed by message content
    const feedResults = feedEntries
      .filter(e => e.message?.toLowerCase().includes(q))
      .slice(0, 5);

    const hasResults = people.length > 0 || groupResults.length > 0 || listResults.length > 0 || tableResults.length > 0 || feedResults.length > 0;
    return hasResults ? { people, groups: groupResults, lists: listResults, tables: tableResults, feed: feedResults } : null;
  }, [query, donors, groups, savedLists, savedTables, feedEntries]);

  // Get avatar color
  const getAvatarColor = (name) => {
    const colors = ['#a78bfa', '#60a5fa', '#34d399', '#2dd4bf', '#818cf8', '#22d3d1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Get initials
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Handle result click
  const handleResultClick = (type, item, extra = null) => {
    onNavigate(type, item, extra);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="search-modal-backdrop" onClick={handleBackdropClick}>
      <div
        className="search-modal"
        ref={modalRef}
        style={position ? {
          position: 'fixed',
          top: position.top,
          left: position.left
        } : undefined}
      >
        {/* Search input */}
        <div className="search-modal-header">
          <svg className="search-modal-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="search-modal-input"
            placeholder="Search people, groups, conversations..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <button className="search-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Results */}
        <div className="search-modal-results">
          {!query.trim() && (
            <div className="search-modal-hint">
              Start typing to search across all of Knowroom
            </div>
          )}

          {query.trim() && !searchResults && (
            <div className="search-modal-empty">
              No results found for "{query}"
            </div>
          )}

          {searchResults?.people?.length > 0 && (
            <div className="search-modal-section">
              <div className="search-modal-section-label">People</div>
              {searchResults.people.map((person, idx) => (
                <button
                  key={idx}
                  className="search-modal-result"
                  onClick={() => handleResultClick('profile', person)}
                >
                  <div
                    className="result-avatar"
                    style={{ borderColor: getAvatarColor(person.name), color: getAvatarColor(person.name) }}
                  >
                    {getInitials(person.name)}
                  </div>
                  <div className="result-info">
                    <div className="result-name">{person.name}</div>
                    <div className="result-meta">
                      {(() => {
                        const personGroup = groups.find(g => g.id === person.groupId || g.id === person.group_id);
                        if (personGroup) return personGroup.name;
                        if (person.amount && person.amount !== 'Unknown') return person.amount;
                        return null;
                      })()}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {searchResults?.groups?.length > 0 && (
            <div className="search-modal-section">
              <div className="search-modal-section-label">Groups</div>
              {searchResults.groups.map((group, idx) => (
                <button
                  key={idx}
                  className="search-modal-result"
                  onClick={() => handleResultClick('group', group)}
                >
                  <div
                    className="result-dot"
                    style={{ background: group.color || '#a78bfa' }}
                  />
                  <div className="result-info">
                    <div className="result-name">{group.name}</div>
                    <div className="result-meta">{group.members?.length || 0} members</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {searchResults?.lists?.length > 0 && (
            <div className="search-modal-section">
              <div className="search-modal-section-label">Lists</div>
              {searchResults.lists.map((list, idx) => (
                <button
                  key={idx}
                  className="search-modal-result"
                  onClick={() => handleResultClick('list', list)}
                >
                  <div className="result-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
                    </svg>
                  </div>
                  <div className="result-info">
                    <div className="result-name">{list.organizationPrompt || 'Untitled List'}</div>
                    <div className="result-meta">{list.groups?.length || 0} groups</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {searchResults?.tables?.length > 0 && (
            <div className="search-modal-section">
              <div className="search-modal-section-label">Tables</div>
              {searchResults.tables.map((table, idx) => (
                <button
                  key={idx}
                  className="search-modal-result"
                  onClick={() => handleResultClick('table', table)}
                >
                  <div className="result-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <path d="M3 9h18M3 15h18M9 3v18"/>
                    </svg>
                  </div>
                  <div className="result-info">
                    <div className="result-name">{table.name || 'Untitled Table'}</div>
                    <div className="result-meta">{table.rows?.length || 0} rows</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {searchResults?.feed?.length > 0 && (
            <div className="search-modal-section">
              <div className="search-modal-section-label">Conversations</div>
              {searchResults.feed.map((entry, idx) => (
                <button
                  key={idx}
                  className="search-modal-result"
                  onClick={() => handleResultClick('feed', null, { scrollToEntryId: entry.id })}
                >
                  <div className="result-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <div className="result-info">
                    <div className="result-name result-message">
                      {entry.message?.slice(0, 80)}{entry.message?.length > 80 ? '...' : ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Keyboard hint */}
        <div className="search-modal-footer">
          <span className="keyboard-hint">
            <kbd>⌘</kbd><kbd>K</kbd> to open
          </span>
          <span className="keyboard-hint">
            <kbd>Esc</kbd> to close
          </span>
        </div>
      </div>
    </div>
  );
}

export default DesktopSearchModal;
